<template>
  <div class="footer-bar">
    <ul class="buttons">
      <li v-for="button in footerBar.buttons" :key="button.uploadType">
        <div v-if="button.type == 'uploadButton'">
          <order-file-uploader
            class="menu-item"
            :upload-file-type="button.uploadType"
            @files-added="handleFilesAdded(button.uploadType)"
            @file-progress="
              (progress) => handleFileProgress(button.uploadType, progress)
            "
            @upload-done="handleUploadDone(button.uploadType)"
            @file-error="handleUploadDone(button.uploadType)"
          >
            <div v-if="!hasFile(button.uploadType)">
              <uploader-btn
                class="pill-button-lg add-button"
                key="pc"
                :id="`${button.uploadType}-upload-btn`"
                :class="{ glow: uploadStates[button.uploadType]?.inProgress }"
                :attrs="{ accept: button.acceptedFormats }"
              >
                <span v-if="uploadStates[button.uploadType]?.inProgress"
                  >Bezig met uploaden
                  {{ uploadStates[button.uploadType]?.progress }}%</span
                >
                <span v-else>{{ button.title }}</span>
              </uploader-btn>
            </div>
            <mailing-file
              v-else
              :file="getFile(button.uploadType)"
              @remove="clearFile(getFile(button.uploadType))"
            ></mailing-file>
          </order-file-uploader>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import OrderFileUploader from '@/components/ui/file-upload/OrderFileUploader.vue'
import MailingFile from './MailingFile.vue'
import { mapStores } from 'pinia'
import { useFileListStore } from '@/stores/FileList.store'

export default {
  name: 'FooterBar',
  props: {
    footerBar: {
      type: Object,
      required: true
    },
    selectedFiles: {
      type: Array
    }
  },
  emits: ['file-type-changed'],
  data() {
    return {
      uploadStates: {}
    }
  },
  methods: {
    handleFilesAdded(uploadType) {
      this.uploadStates[uploadType] = {
        inProgress: true,
        progress: 0
      }
    },
    handleFileProgress(uploadType, progress) {
      // If progress is a file object from vue-simple-uploader, extract the actual progress
      let actualProgress = progress

      if (typeof progress === 'object' && progress !== null) {
        // vue-simple-uploader file object has _prevProgress property (0-1 range)
        if (progress._prevProgress !== undefined) {
          actualProgress = Math.floor(progress._prevProgress * 100)
        } else {
          actualProgress = 0
        }
      }

      if (this.uploadStates[uploadType]) {
        this.uploadStates[uploadType].progress = actualProgress
      }
    },
    handleUploadDone(uploadType) {
      this.uploadStates[uploadType] = {
        inProgress: false,
        progress: 0
      }

      // Add uploaded file reference to all selected files
      // Use getFileFromList instead of getFile to avoid circular dependency
      const uploadedFile = this.getFileFromList(uploadType)
      if (uploadedFile && this.selectedFiles) {
        this.selectedFiles.forEach((selectedFile) => {
          // Make sure productSpecs exists
          if (!selectedFile.productSpecs) {
            selectedFile.productSpecs = {}
          }

          // Initialize files array in productSpecs if it doesn't exist
          if (!selectedFile.productSpecs.files) {
            selectedFile.productSpecs.files = []
          }

          // Check if this file reference already exists
          const existingFileIndex = selectedFile.productSpecs.files.findIndex(
            (f) => f.type === uploadType && f.name === uploadedFile.fileName
          )

          // Only add if it doesn't exist yet
          if (existingFileIndex === -1) {
            selectedFile.productSpecs.files.push({
              type: uploadType,
              name: uploadedFile.fileName
            })
          }

          if (uploadType === 'ADDRESS_LIST') {
            selectedFile.quantity = uploadedFile.quantity
          }
        })

        // Trigger price calculation once after all files are updated
        if (uploadType === 'ADDRESS_LIST' || uploadType === 'ATTACHMENT_FILE') {
          this.fileListStore.setProductOrderPrice({
            fileNames: this.selectedFiles.map((f) => f.fileName),
            spec: {},
            hydrate: false
          })
        }

        this.$emit('file-type-changed')
      }
    },
    hasFile(uploadType) {
      return !!this.getFile(uploadType)
    },
    // Get file from fileList by uploadType that is NOT already linked to other files
    getFileFromList(uploadType) {
      // Get all files with this uploadType
      const filesWithType = this.fileListStore.fileList.filter(
        (file) => file.uploadFileType === uploadType
      )

      if (filesWithType.length === 0) return null
      if (filesWithType.length === 1) return filesWithType[0]

      // If multiple files exist, find one that's not already linked to another file
      // or not linked to the current selection
      const currentlyLinkedFile = this.getFile(uploadType)

      // Return a file that is not currently linked, prefer the most recent one
      const unlinkedFile = filesWithType
        .reverse()
        .find((file) => !currentlyLinkedFile || file.uuid !== currentlyLinkedFile.uuid)

      return unlinkedFile || filesWithType[0]
    },
    // Get file that is actually linked to the selected files
    getFile(uploadType) {
      // If no selected files, no file can be shown
      if (!this.selectedFiles || this.selectedFiles.length === 0) return null

      // Get the file reference from the first selected file's productSpecs
      const fileReference = this.selectedFiles[0].productSpecs?.files?.find(
        (f) => f.type === uploadType
      )

      if (!fileReference) return null

      // Find the actual file in the fileList that matches this reference
      return this.fileListStore.fileList.find(
        (file) =>
          file.uploadFileType === fileReference.type &&
          file.fileName === fileReference.name
      )
    },
    async clearFile(file) {
      // Remove file reference from all selected files
      if (this.selectedFiles && file.uploadFileType) {
        this.selectedFiles.forEach((selectedFile) => {
          if (selectedFile.productSpecs && selectedFile.productSpecs.files) {
            selectedFile.productSpecs.files = selectedFile.productSpecs.files.filter(
              (f) =>
                !(f.type === file.uploadFileType && f.name === file.fileName)
            )
          }

          selectedFile.quantity = 1

          this.fileListStore.setProductOrderPrice({
            fileNames: this.selectedFiles.map((f) => f.fileName),
            spec: {},
            hydrate: false
          })
        })
      }

      await this.fileListStore.removeFile(file.uuid)
      this.$emit('file-type-changed')
    }
  },
  computed: {
    ...mapStores(useFileListStore)
  },
  components: {
    OrderFileUploader,
    MailingFile
  }
}
</script>

<style lang="scss" scoped>
.footer-bar {
  padding-top: 10px;
}
.add-button {
  background-color: var(--arch-color);
  margin-right: 0;
  cursor: pointer;
  width: 100%;
}

.menu-item {
  width: 274px;
}

.buttons {
  display: flex;
  gap: 20px;
}
</style>
